import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PageConfig from '@/component/layout/PageConfig.tsx'
import DashboardMap from '@/pages/dashboard/DashboardMapDefault.tsx'
import UrgentNotification from '@/pages/dashboard/UrgentNotification.tsx'
import WanderingNotification from '@/pages/dashboard/WanderingNotification.tsx'
import { isNotEmptyArray } from '@/util/array.ts'
import useQueryGetUsersAll from '@/api/user/getUsersAll.ts'
import { IReqGetUsersBbox } from '@/api/user/getUsersBbox.ts'
import { AlertStatus, DeviceStatus } from '@/api/user/getUsers.ts'
import { WanderingStatus } from '@/util/location.ts'
import useQueryGetMe, { DashboardType } from '@/api/auth/getMe.ts'
import useQueryGetAlertBioLive from '@/api/notification/getAlertBioLive.ts'
import BioAlertDetailModal from '@/pages/notification/bio/BioAlertModal.tsx'
import useQueryGetMyPermission, { PermissionAction, PermissionSubject } from '@/api/auth/getPermission.ts'
import AlertStatusBadge from '@/component/badge/AlertStatusBadge.tsx'
import WanderingStatusBadge from '@/component/badge/WanderingStatusBadge.tsx'
import useQueryGetLiveEmergencyAlert from '@/api/notification/getLiveEmergencyAlert.ts'
import useQueryGetLiveFallDetectionAlert from '@/api/notification/getLiveFallDetectionAlert.ts'
import DeviceStatusBadge from '@/component/badge/DeviceStatusBadge.tsx'
import DashboardMapForCareManager from '@/pages/dashboard/DashboardMapForCareManager.tsx'
import { useTranslation } from 'react-i18next'
import useQueryGetUsersDeviceStatus from '@/api/user/getUsersDeviceStatus'
import { useMapLocationStore } from '@/store/MapLocationStore.ts'


type IProps = {}

const Dashboard: React.FC<IProps> = ({}) => {
    const { t } = useTranslation()
    const { hasPermission } = useQueryGetMyPermission()
    const [bbox, setBbox] = useState<IReqGetUsersBbox>()
    
    // 전체 사용자 리스트를 한 번만 가져오기 (5분마다 갱신)
    const { data: allUsers } = useQueryGetUsersAll()
    
    // 클라이언트에서 bbox 필터링 (API 호출 없음!)
    const users = useMemo(() => {
        if (!allUsers || !bbox) return []
        
        const minLat = Math.min(bbox.startLat, bbox.endLat)
        const maxLat = Math.max(bbox.startLat, bbox.endLat)
        const minLng = Math.min(bbox.startLng, bbox.endLng)
        const maxLng = Math.max(bbox.startLng, bbox.endLng)
        
        return allUsers.filter((user) => {
            if (!user.location) return false
            const lat = user.location.latitude
            const lng = user.location.longitude
            return lat >= minLat && lat <= maxLat && lng >= minLng && lng <= maxLng
        })
    }, [allUsers, bbox])
    
    const {
        data: bioAlertRes,
        refetch: refetchBioAlerts
    } = useQueryGetAlertBioLive(hasPermission(PermissionSubject.ALERT_BIO, PermissionAction.READ))
    const { data: me } = useQueryGetMe()
    const { data: deviceStatus } = useQueryGetUsersDeviceStatus()
    const { data: emergencyAlerts } = useQueryGetLiveEmergencyAlert()
    const { data: fallDetectionAlerts } = useQueryGetLiveFallDetectionAlert()
    const mapRef = useRef<any>()
    const { consumeTargetLocation } = useMapLocationStore()

    const totalUserDeviceCount = useMemo(() => {
        if (!deviceStatus) return 0
        return Object.values(deviceStatus).reduce((sum, currentValue) => sum + currentValue, 0);
    }, [deviceStatus])

    const markers = useMemo(() => {
        return users?.filter((user) => user?.location)
    }, [users])

    // 배회 상태별 카운트 계산
    const wanderingStats = useMemo(() => {
        if (!users) return { wandering: 0, safe: 0, unknown: 0 }
        
        const stats = {
            wandering: 0,
            safe: 0,
            unknown: 0
        }
        
        users.forEach(user => {
            if (user.wanderingStatus === WanderingStatus.WANDERING) {
                stats.wandering++
            } else if (user.wanderingStatus === WanderingStatus.SAFE) {
                stats.safe++
            } else {
                stats.unknown++
            }
        })
        
        return stats
    }, [users])

    // 배회 이탈 사용자 목록
    const wanderingUsers = useMemo(() => {
        if (!users) return []
        return users.filter(user => user.wanderingStatus === WanderingStatus.WANDERING)
    }, [users])

    const [detailModalId, setDetailModalId] = useState<number>(-1)
    const detailModalRef = useRef<any>()

    // const markers = useMemo(() => {
    //     return users?.filter(item => {
    //         // 실시간 상태 데이터가 있는 경우 우선 사용
    //         // TODO: 배터리 통계 계산 로직 추가
    //         // const recentState = item.recentState?.summary
    //         // if (recentState) {
    //         //     if (recentState.isCharging) return true
    //         //     if (recentState.isOnline !== undefined) return true
    //         // }
    //         return item?.deviceStatus
    //     })
    // }, [users])

    const isCareManager = useMemo(
        () => me?.dashboardType == DashboardType.CARE_MANAGER,
        [me?.dashboardType]
    )

    // 실시간 상태를 고려한 디바이스 상태 계산
    // const getActualDeviceStatus = (user: IUserBbox) => {
        
        
    //     // TODO: 배터리 통계 계산 로직 추가
    //     // const recentState = user.recentState?.summary
        
    //     // 실시간 충전 상태 우선 확인
    //     // if (recentState?.isCharging) return DeviceStatus.CHARGING
        
    //     // 실시간 온라인 상태 확인
    //     // if (recentState?.isOnline !== undefined) {
    //         // return recentState.isOnline ? DeviceStatus.ONLINE : DeviceStatus.OFFLINE
    //     // }
        
    //     // 기본 device.status 사용
    //     return user.deviceStatus
    // }

    // 배터리 통계 계산
    // TODO: 배터리 통계 계산 로직 추가
    // const batteryStats = useMemo(() => {
    //     if (!users) return { lowBatteryUsers: [] }
        
    //     const lowBatteryUsers = users.filter(user => {
    //         const battery = user.recentState?.summary?.battery ?? user.device?.battery
    //         const status = getActualDeviceStatus(user)
    //         return battery !== undefined && 
    //                battery < 15 && 
    //                status !== DeviceStatus.CHARGING
    //     })

    //     return { lowBatteryUsers }
    // }, [users])

    const handleBoundsChange = useCallback((newBbox: IReqGetUsersBbox) => {
        setBbox(currentBbox => {
            // 새로 계산된 영역이 현재 영역과 다를 때만 상태를 업데이트하여 무한 루프를 방지합니다.
            if (JSON.stringify(currentBbox) === JSON.stringify(newBbox)) {
                return currentBbox;
            }
            return newBbox;
        });
    }, []);

    // 알림에서 위치 버튼을 클릭했을 때 지도 이동
    useEffect(() => {
        const targetLocation = consumeTargetLocation()
        if (targetLocation && mapRef.current) {
            const { latitude, longitude } = targetLocation
            // 위도/경도 값이 유효한지 확인
            if (
                typeof latitude === 'number' && 
                typeof longitude === 'number' && 
                !isNaN(latitude) && 
                !isNaN(longitude) &&
                isFinite(latitude) && 
                isFinite(longitude)
            ) {
                mapRef.current.setLocation(latitude, longitude, 16)
            } else {
                console.warn('Invalid target location coordinates:', targetLocation)
            }
        }
    }, [consumeTargetLocation])
    return <>
        <PageConfig showTopNav={true} showSideNav={true} className={'min-w-[800px]'}>
            <div className={'relative h-full overflow-hidden'}>
                {!isCareManager ? <DashboardMap users={markers} className={'h-full w-full'} ref={mapRef} onBoundsChange={handleBoundsChange} /> :
                    <DashboardMapForCareManager users={markers} className={'h-full w-full'} ref={mapRef} onBoundsChange={handleBoundsChange} />}

                <div
                    className={'w-full flex h-full justify-between overflow-hidden absolute top-12 pointer-events-none'}>
                    <div className={'flex flex-col gap-[15px] items-start pl-[15px] pt-[15px] h-full'}
                         style={{ width: '100%' }}>
                        <div className={'flex gap-[15px]'}>
                            {isCareManager ? <div
                                className={'bg-white px-[18px] py-[21px] border border-solid border-gray2 flex flex-col w-[280px] font-medium gap-[8px]'}>
                                <p className={'text-center px-[10px] py-[8px] text-lg font-bold'}>{t('V3.dashboard.totalUsers', { count: totalUserDeviceCount })}</p>
                                <div className={'w-full h-[1px] bg-gray2 my-[8px]'} />
                                
                                {/* 배회 상태별 카운트 (최우선) */}
                                <div className={'px-[10px] py-[8px] flex items-center gap-[5px]'}>
                                    <WanderingStatusBadge value={WanderingStatus.WANDERING} />
                                    <p>{t('dashboard.summary.wandering', { count: wanderingStats.wandering })}</p>
                                </div>
                                <div className={'px-[10px] py-[8px] flex items-center gap-[5px]'}>
                                    <WanderingStatusBadge value={WanderingStatus.SAFE} />
                                    <p>{t('dashboard.summary.safe', { count: wanderingStats.safe })}</p>
                                </div>
                                
                                <div className={'w-full h-[1px] bg-gray2 my-[8px]'} />
                                
                                {/* 디바이스 상태별 카운트 */}
                                <div className={'px-[10px] py-[8px] flex items-center gap-[5px]'}>
                                    <DeviceStatusBadge value={DeviceStatus.EMERGENCY} />
                                    <p>{t('V3.dashboard.status.emergency', { count: deviceStatus?.emergency || 0 })}</p>
                                </div>
                                <div className={'px-[10px] py-[8px] flex items-center gap-[5px]'}>
                                    <DeviceStatusBadge value={DeviceStatus.ONLINE} />
                                    <p>{t('V3.dashboard.status.online', { count: deviceStatus?.online || 0 })}</p>
                                </div>
                                <div className={'px-[10px] py-[8px] flex items-center gap-[5px]'}>
                                    <DeviceStatusBadge value={DeviceStatus.CHARGING} />
                                    <p>{t('V3.dashboard.status.charging', { count: deviceStatus?.charging || 0 })}</p>
                                </div>
                                <div className={'px-[10px] py-[8px] flex items-center gap-[5px]'}>
                                    <DeviceStatusBadge value={DeviceStatus.OFFLINE} />
                                    <p>{t('V3.dashboard.status.offline', { count: deviceStatus?.offline || 0 })}</p>
                                </div>



                                {me && me?.latitude && me?.longitude &&
                                    <button className={'pointer-events-auto py-[8.5px] text-white bg-main_b mt-2'}
                                            onClick={() => {
                                                const latitude = me.latitude
                                                const longitude = me.longitude
                                                
                                                if (
                                                    typeof latitude === 'number' && 
                                                    typeof longitude === 'number' && 
                                                    !isNaN(latitude) && 
                                                    !isNaN(longitude) &&
                                                    isFinite(latitude) && 
                                                    isFinite(longitude)
                                                ) {
                                                    mapRef?.current?.setLocation(latitude, longitude, 18)
                                                } else {
                                                    console.warn('Center location not available or invalid:', { latitude, longitude })
                                                }
                                            }}>{t('dashboard.summary.findCenterLocation')}</button>}
                            </div> : <div
                                className={'bg-white px-[18px] py-[21px] border border-solid border-gray2 flex flex-col font-medium gap-[8px]'}>
                                <p className={'text-center px-[10px] py-[8px]'}>{t('dashboard.summary.title', { count: totalUserDeviceCount })}</p>
                                <div className={'w-full h-[1px] bg-gray2 my-[8px]'} />
                                
                                {/* 배회 상태 (최우선) */}
                                <div className={'px-[10px] py-[8px] flex items-center gap-[5px]'}>
                                    <WanderingStatusBadge value={WanderingStatus.WANDERING} />
                                    <p>{t('dashboard.summary.wandering', { count: wanderingStats.wandering })}</p>
                                </div>
                                <div className={'px-[10px] py-[8px] flex items-center gap-[5px]'}>
                                    <WanderingStatusBadge value={WanderingStatus.SAFE} />
                                    <p>{t('dashboard.summary.safe', { count: wanderingStats.safe })}</p>
                                </div>
                                
                                <div className={'w-full h-[1px] bg-gray2 my-[8px]'} />
                                
                                {/* 긴급 알림 */}
                                <div className={'px-[10px] py-[8px] flex items-center gap-[5px]'}>
                                    <AlertStatusBadge value={AlertStatus.EMERGENCY} />
                                    <p>{t('dashboard.summary.emergency', { count: (emergencyAlerts?.emergencies?.length || 0) + (fallDetectionAlerts?.falls?.length || 0) })}</p>
                                </div>
                                <div className={'px-[10px] py-[8px] flex items-center gap-[5px]'}>
                                    <AlertStatusBadge value={AlertStatus.BIO} />
                                    <p>{t('dashboard.summary.bio', { count: bioAlertRes?.bios?.length || 0 })}</p>
                                </div>
                                


                                {me && me?.latitude && me?.longitude &&
                                    <button className={'pointer-events-auto py-[8.5px] text-white bg-main_b'}
                                            onClick={() => {
                                                const latitude = me.latitude
                                                const longitude = me.longitude
                                                
                                                if (
                                                    typeof latitude === 'number' && 
                                                    typeof longitude === 'number' && 
                                                    !isNaN(latitude) && 
                                                    !isNaN(longitude) &&
                                                    isFinite(latitude) && 
                                                    isFinite(longitude)
                                                ) {
                                                    mapRef?.current?.setLocation(latitude, longitude, 18)
                                                } else {
                                                    console.warn('Center location not available or invalid:', { latitude, longitude })
                                                }
                                            }}>{t('dashboard.summary.findCenterLocation')}</button>}
                            </div>}
                        </div>

                        {/* 생체 이상 알림 - 좌측 중하단 */}
                        <div className={'flex-1 flex flex-col justify-end pb-[15px] pointer-events-auto overflow-y-auto max-h-[50%]'}>
                            {hasPermission(PermissionSubject.ALERT_BIO, PermissionAction.READ) && bioAlertRes && isNotEmptyArray(bioAlertRes.bios) && bioAlertRes.bios?.map(item =>
                                <UrgentNotification key={item?.id}
                                          className={'shadow-lg mb-[10px]'}  {...item}
                                          onRefreshList={refetchBioAlerts}
                                          onClickMarker={(latitude, longitude) => {
                                              if (
                                                  typeof latitude === 'number' &&
                                                  typeof longitude === 'number' &&
                                                  !isNaN(latitude) &&
                                                  !isNaN(longitude) &&
                                                  isFinite(latitude) &&
                                                  isFinite(longitude)
                                              ) {
                                                  mapRef.current?.setLocation(latitude, longitude, 18)
                                              } else {
                                                  console.warn('Alert location not available or invalid:', { latitude, longitude })
                                              }
                                          }}
                                          onClickItem={id => {
                                              setDetailModalId(id)
                                              detailModalRef.current?.show()
                                          }}
                                />)}
                        </div>
                    </div>
                    <div
                        className={'flex flex-col overflow-y-auto items-end'}
                        style={{ position: 'relative', left: '-40px', width: '100%', height: '92%' }} >
                        <div className={'flex flex-col pointer-events-auto pr-[15px] pt-[15px]'}>
                            {/* 배회 이탈 알림 */}
                            {wanderingUsers.map(user => (
                                <WanderingNotification
                                    key={`wandering_${user.id}`}
                                    id={user.id}
                                    userId={user.id}
                                    userName={user.name}
                                    distance={user.distanceFromHome}
                                    location={user.location}
                                    timestamp={user.location?.timestamp}
                                    className={'shadow-lg mb-[10px]'}
                                    onClickLocation={(latitude, longitude) => {
                                        if (
                                            typeof latitude === 'number' &&
                                            typeof longitude === 'number' &&
                                            !isNaN(latitude) &&
                                            !isNaN(longitude) &&
                                            isFinite(latitude) &&
                                            isFinite(longitude)
                                        ) {
                                            mapRef.current?.setLocation(latitude, longitude, 18)
                                        }
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </PageConfig>
        <BioAlertDetailModal ref={detailModalRef} defaultId={detailModalId} refetchAlerts={refetchBioAlerts} />
    </>
}
export default Dashboard